import { useEffect, useState } from "react";
import { Loader2, Wand2, Store, Save, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

// Distribución automática: jala las líneas de una OC y sugiere el reparto por
// tienda ponderado por la venta histórica (o equitativo si no hay ventas).
export default function RetailAutoDistribution() {
  const [pos, setPos] = useState([]);
  const [pid, setPid] = useState("");
  const [dist, setDist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/retail/purchase-orders").then((r) => setPos(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const compute = async () => {
    if (!pid) return;
    setBusy("calc"); setDist(null);
    try {
      const { data } = await api.get(`/retail/purchase-orders/${pid}/auto-distribution`);
      setDist(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo calcular"); } finally { setBusy(""); }
  };

  const apply = async () => {
    if (!dist) return;
    setBusy("apply");
    try {
      const payload = { lineas: dist.lineas.map((l) => ({ sku: l.sku, descripcion: l.descripcion, por_tienda: l.por_tienda })) };
      const { data } = await api.put(`/retail/purchase-orders/${pid}/distribution`, payload);
      toast.success(`Distribución aplicada a la OC · ${data.piezas} piezas`);
      setPos((ps) => ps.map((p) => p.id === pid ? { ...p, tiene_distribucion: true } : p));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al aplicar"); } finally { setBusy(""); }
  };

  const stores = dist ? Array.from(new Set(dist.lineas.flatMap((l) => l.por_tienda.map((p) => `${p.tienda}|${p.nombre}`)))) : [];

  if (loading) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="retail-auto-dist">
      <div className="card-surface p-4 mb-4">
        <div className="flex items-center gap-2 overline text-navy/40 mb-3"><Wand2 className="w-3.5 h-3.5" /> Distribución automática desde una OC</div>
        {pos.length === 0 ? (
          <p className="text-sm text-navy/50">No hay órdenes de compra. Crea una en <b>RETAIL Studio → Órdenes de Compra</b> o genera una automática desde el <b>Pronóstico de Demanda</b>.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <select value={pid} onChange={(e) => { setPid(e.target.value); setDist(null); }} className="field flex-1 min-w-[240px]" data-testid="autodist-po">
              <option value="">Selecciona una orden de compra…</option>
              {pos.map((p) => <option key={p.id} value={p.id}>{p.folio || p.id} · {p.proveedor || "—"} · {p.piezas} pzs{p.tiene_distribucion ? " ✓" : ""}</option>)}
            </select>
            <button onClick={compute} disabled={!pid || busy === "calc"} className="btn btn-primary" data-testid="autodist-calc">
              {busy === "calc" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Calcular reparto
            </button>
          </div>
        )}
      </div>

      {dist && (
        <div data-testid="autodist-result">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-sm text-navy/60">
              Método: <b>{dist.metodo === "venta_historica" ? "Venta histórica por tienda" : "Reparto equitativo"}</b> · {dist.tiendas} tienda(s) · {dist.piezas} piezas
            </div>
            <button onClick={apply} disabled={busy === "apply"} className="btn btn-primary text-sm" data-testid="autodist-apply">
              {busy === "apply" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Aplicar a la OC
            </button>
          </div>
          <div className="card-surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy/10 text-[11px] text-navy/40 uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5 sticky left-0 bg-white">SKU / Producto</th>
                  {stores.map((s) => { const [num, nom] = s.split("|"); return <th key={s} className="text-right px-3 py-2.5 whitespace-nowrap"><Store className="w-3 h-3 inline mr-1" />{num} · {nom}</th>; })}
                  <th className="text-right px-4 py-2.5">Total</th>
                </tr>
              </thead>
              <tbody>
                {dist.lineas.map((l) => (
                  <tr key={l.sku} className="border-b border-navy/5 last:border-0" data-testid={`autodist-row-${l.sku}`}>
                    <td className="px-4 py-2.5 sticky left-0 bg-white"><div className="truncate max-w-[220px]">{l.descripcion || l.sku}</div><div className="text-[11px] text-navy/40">{l.sku}</div></td>
                    {stores.map((s) => { const [num] = s.split("|"); const p = l.por_tienda.find((x) => String(x.tienda) === String(num)); return <td key={s} className={`text-right px-3 py-2.5 ${p?.piezas ? "" : "text-navy/25"}`}>{p?.piezas || 0}</td>; })}
                    <td className="text-right px-4 py-2.5 font-medium">{l.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-navy/40 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Al aplicar se guarda en la distribución de la OC (visible en la orden y en su PDF).</p>
        </div>
      )}
    </div>
  );
}
